import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import { handleError } from '../utils'

const FETCH_REQUEST = 'contenthouse/userV3/FETCH_REQUEST'
const FETCH_SUCCESS = 'contenthouse/userV3/FETCH_SUCCESS'
const FETCH_FAILURE = 'contenthouse/userV3/FETCH_FAILURE'

const CREATE_REQUEST = 'contenthouse/userV3/CREATE_REQUEST'
const CREATE_SUCCESS = 'contenthouse/userV3/CREATE_SUCCESS'
const CREATE_FAILURE = 'contenthouse/userV3/CREATE_FAILURE'

const UPDATE_REQUEST = 'contenthouse/userV3/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'contenthouse/userV3/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'contenthouse/userV3/UPDATE_FAILURE'

const DELETE_REQUEST = 'contenthouse/userV3/DELETE_REQUEST'
const DELETE_SUCCESS = 'contenthouse/userV3/DELETE_SUCCESS'
const DELETE_FAILURE = 'contenthouse/userV3/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loading: false,
  result: {},
  updating: false,
}

// Actions
export function fetchRequest(){
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    payload,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadUsersV3(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/v3/users', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data.results, Schemas.USERV3_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadBlacklistedProvidersV3(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    // Use Client id
    const promise = api(`/v3/clients/${options.clientId}/blacklisted-providers`, { size: options.size })
      .then(({ data }) => {
        const normalizedJson = normalize(data.results, Schemas.USERV3_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(data))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadUserV3(id, dispatch){
  dispatch(fetchRequest())

  const promise = api(`/v3/users/${id}`, {})
    .then(({ data }) => {
      const normalizedJson = normalize(data, Schemas.USERV3)
      dispatch(updateEntities(normalizedJson))
      dispatch(fetchSuccess())

      return { success: true, data }
    })
    .catch((data) => {
      const errors = handleError(data)
      dispatch(fetchFailure(errors))

      return { success: false, errors }
    })

  return promise
}

export function createUserV3(user, options = {}){
  const config = {
    method: 'POST',
    data: user,
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/v3/users', options, config)
      .then((request) => {
        dispatch(createSuccess())
        return loadUserV3(request.data.id, dispatch)
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateUserV3(user, options = {}){
  const config = {
    method: 'PUT',
    data: user,
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/v3/users/${user.id}`, options, config)
      .then(({ data }) => {
        const updatedData = data
        if (!updatedData.flags){
          updatedData.flags = null
        }

        const normalizedJson = normalize(updatedData, Schemas.USERV3)

        const updateEntityOptions = {
          mergeOptions: {
            customMerge: (key) => {
              if (['editor', 'provider', 'qc'].includes(key)){
                return (_, source) => source
              }

              return undefined
            },
          },
        }

        dispatch(updateEntities(normalizedJson, updateEntityOptions))
        dispatch(updateSuccess())
        return { success: true, updatedData }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteUserV3(user){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/v3/users/${user.id}`, { permanent: true }, config)
      .then(({ data }) => {
        const normalizedJson = normalize(user, Schemas.USERV3)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, result: data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })
    return promise
  }
}

export function loadZoneProvidersV3(zoneId, options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/v3/zones/${zoneId}/providers`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data.results, Schemas.USERV3_ARRAY)
        dispatch(updateEntities(normalizedJson))

        const zoneProviders = {
          linkedProviders: data.results,
        }
        dispatch(fetchSuccess(zoneProviders))

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  const { type, payload } = action

  switch (type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
        result: payload,
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
